import { linkToOutletPoints, type OutletPointLayer } from "./gisLayer.ts";
import { nextId, uniqueName, updateSubBasins } from "./entityHelpers.ts";
import { importEntitiesFromGeo, type GisColumnMapping } from "./geoImport.ts";
import { addZone, assignZoneInBulk } from "./zoneEntities.ts";
import type { EvalHidProject, OutletPoint, Zone } from "./types.ts";
import { TranslatableError, type Translate } from "../i18n/translate.ts";

export function addOutletPoint(project: EvalHidProject, name: string): EvalHidProject {
  const cleanName = name.trim();
  if (!cleanName) throw new TranslatableError("projectEntities.emptyOutletPointName");
  const finalName = uniqueName(
    cleanName,
    project.outletPoints.map((p) => p.name)
  );
  const point: OutletPoint = {
    outletCode: nextId(project.outletPoints, (p) => p.outletCode),
    name: finalName,
    gisCode: null,
    aquatoolCode: null,
  };
  return { ...project, outletPoints: [...project.outletPoints, point] };
}

/** Existing RUNOFF zone with this exact name, if any — a same-named SNOW-only zone doesn't count (same distinction `addZone`'s own duplicate check makes: a name can be reused once per kind). */
function findRunoffZoneByName(project: EvalHidProject, name: string): Zone | undefined {
  return project.zones.find((z) => z.name === name && z.modelTypeCode != null);
}

/**
 * Quick-start shortcut, offered both in the wizard (Step3Outlets.tsx) and
 * in EditBar.tsx's "Outlet points" catalog (OutletPointsPanel.tsx): creates
 * one outlet point per RUNOFF zone already defined in "2. Zones" (snow zones are
 * skipped — an outlet point is somewhere water actually drains to, a snow
 * zone doesn't have one of its own) and assigns each one to every
 * sub-basin that already has that zone — the same grouping already done a
 * step earlier, so it doesn't have to be redone by hand for outlet points
 * too when the two happen to line up. Sub-basins with no runoff zone are
 * left untouched. Idempotent by name: an outlet point that already
 * matches a zone's name is reused (just re-assigned) instead of
 * duplicated, so running it again after adding more zones only creates
 * what's missing — both buttons grey themselves out once there's nothing
 * left to catch up on, see `hasUnreplicatedZones` below. The reverse
 * direction — outlet points to zones — is `replicateOutletPointsAsZones`.
 */
export function replicateZonesAsOutletPoints(project: EvalHidProject): EvalHidProject {
  const runoffZones = project.zones.filter((z) => z.modelTypeCode != null);
  return runoffZones.reduce((proj, zone) => {
    const existing = proj.outletPoints.find((p) => p.name === zone.name);
    const withOutlet = existing ? proj : addOutletPoint(proj, zone.name);
    const outletCode = existing
      ? existing.outletCode
      : withOutlet.outletPoints[withOutlet.outletPoints.length - 1].outletCode;
    const subBasinCodes = new Set(
      withOutlet.subBasins.filter((c) => c.zoneCode === zone.zoneCode).map((c) => c.subBasinCode)
    );
    return assignSurfaceOutletInBulk(withOutlet, subBasinCodes, outletCode);
  }, project);
}

/**
 * Whether `replicateZonesAsOutletPoints` would actually create or reassign
 * anything right now — used by its button (Step3Outlets.tsx, and
 * OutletPointsPanel.tsx's own copy of the same shortcut) to grey itself out
 * instead of staying permanently enabled: once every runoff zone already
 * has a matching-named outlet point with all its sub-basins assigned,
 * clicking again is a costless no-op that still reads as "did this do
 * anything?" to the user. Mirrors the same per-zone existing/subBasinCodes
 * logic above, cheaply (no need to run the real replication and diff the
 * result). Re-enables itself as soon as something changes that the button
 * would need to catch up on — a new zone, or a sub-basin re-assigned to a
 * different zone.
 */
export function hasUnreplicatedZones(project: EvalHidProject): boolean {
  const runoffZones = project.zones.filter((z) => z.modelTypeCode != null);
  return runoffZones.some((zone) => {
    const outlet = project.outletPoints.find((p) => p.name === zone.name);
    if (!outlet) return true;
    return project.subBasins.some(
      (c) => c.zoneCode === zone.zoneCode && c.outletCode !== outlet.outletCode
    );
  });
}

/**
 * The reverse of `replicateZonesAsOutletPoints`, offered in EditBar.tsx's
 * "Zones" catalog (Step2Zones.tsx): creates one RUNOFF zone per outlet
 * point already defined and assigns it to every sub-basin that already
 * drains there — the mirror-image shortcut for a project built the other
 * way round (outlet points defined first, zones not yet). Every zone
 * actually CREATED this call gets the same given model — outlet points
 * don't carry one of their own to copy, unlike the other direction, so the
 * caller has to supply it (a model selector next to the button). Idempotent
 * by name, same reasoning as the other direction: reuses an existing
 * RUNOFF zone with a matching name (a same-named snow zone doesn't count)
 * instead of duplicating it — an existing zone keeps whatever model it
 * already had, `modelTypeCode` only applies to zones this call creates from
 * scratch.
 */
export function replicateOutletPointsAsZones(
  project: EvalHidProject,
  modelTypeCode: number
): EvalHidProject {
  return project.outletPoints.reduce((proj, outlet) => {
    const existing = findRunoffZoneByName(proj, outlet.name);
    const withZone = existing ? proj : addZone(proj, outlet.name, modelTypeCode, 0);
    const zoneCode = existing
      ? existing.zoneCode
      : withZone.zones[withZone.zones.length - 1].zoneCode;
    const subBasinCodes = new Set(
      withZone.subBasins
        .filter((c) => c.outletCode === outlet.outletCode)
        .map((c) => c.subBasinCode)
    );
    return assignZoneInBulk(withZone, subBasinCodes, "zoneCode", zoneCode);
  }, project);
}

/**
 * Whether `replicateOutletPointsAsZones` would actually create or reassign
 * anything right now — used by its button (Step2Zones.tsx) to grey itself
 * out, same reasoning as `hasUnreplicatedZones` above, mirrored: once every
 * outlet point already has a matching-named RUNOFF zone with all its
 * sub-basins assigned, there's nothing left to catch up on. Deliberately
 * independent of whether a model is currently selected in the button's own
 * dropdown — that's a separate precondition, validated when the button is
 * actually clicked (see Step2Zones.tsx), not folded into this check.
 */
export function hasUnreplicatedOutletPoints(project: EvalHidProject): boolean {
  return project.outletPoints.some((outlet) => {
    const zone = findRunoffZoneByName(project, outlet.name);
    if (!zone) return true;
    return project.subBasins.some(
      (c) => c.outletCode === outlet.outletCode && c.zoneCode !== zone.zoneCode
    );
  });
}

/** Renames an already-created outlet point. */
export function renameOutletPoint(
  project: EvalHidProject,
  outletCode: number,
  name: string
): EvalHidProject {
  const cleanName = name.trim();
  if (!cleanName) throw new TranslatableError("projectEntities.emptyOutletPointName");
  const finalName = uniqueName(
    cleanName,
    project.outletPoints.filter((p) => p.outletCode !== outletCode).map((p) => p.name)
  );
  return {
    ...project,
    outletPoints: project.outletPoints.map((p) =>
      p.outletCode === outletCode ? { ...p, name: finalName } : p
    ),
  };
}

/**
 * Permanent delete (like PT_Desag.vb/DG_Desagues_UserDeletedRow in the
 * original): removes the outlet point and cascades the unassignment (to
 * `null`, the sentinel both SubBasin.outletCode and Aquifer.outletCode use)
 * on any sub-basin or aquifer that had it as their outlet — the original
 * only cascades to SubBasin.outletCode; here it also cascades to
 * Aquifer.outletCode for consistency, even though the original leaves that
 * reference dangling. No undo.
 */
export function deleteOutletPoint(project: EvalHidProject, outletCode: number): EvalHidProject {
  return {
    ...project,
    outletPoints: project.outletPoints.filter((p) => p.outletCode !== outletCode),
    subBasins: project.subBasins.map((c) => ({
      ...c,
      outletCode: c.outletCode === outletCode ? null : c.outletCode,
    })),
    aquifers: project.aquifers.map((a) => ({
      ...a,
      outletCode: a.outletCode === outletCode ? null : a.outletCode,
    })),
  };
}

/** Assigns the surface outlet point to several sub-basins at once (like AsigPDS.vb), or removes it (outletCode=null). */
export function assignSurfaceOutletInBulk(
  project: EvalHidProject,
  subBasinCodes: ReadonlySet<number>,
  outletCode: number | null
): EvalHidProject {
  return updateSubBasins(project, subBasinCodes, (c) => ({ ...c, outletCode }));
}

export function importOutletPointsFromGeo(
  project: EvalHidProject,
  geojson: GeoJSON.FeatureCollection,
  mapping: GisColumnMapping,
  t: Translate,
  existingGeometries?: ReadonlySet<string>
): { project: EvalHidProject; newLayer: OutletPointLayer } {
  const firstCode = nextId(project.outletPoints, (p) => p.outletCode);
  const { newEntities: newOutletPoints, newLayer } = importEntitiesFromGeo(
    geojson,
    existingGeometries,
    project.outletPoints,
    (p) => p.gisCode,
    (f, gisCode, i) => {
      const props = f.properties ?? {};
      const finalName = uniqueName(
        String(
          props[mapping.nameField] ?? t("projectEntities.defaultOutletPointName", { n: gisCode })
        ),
        [...project.outletPoints.map((p) => p.name)]
      );
      return {
        outletCode: firstCode + i,
        name: finalName,
        gisCode,
        aquatoolCode: null,
      };
    },
    linkToOutletPoints
  );

  const outletPoints = [...project.outletPoints, ...newOutletPoints];
  return { project: { ...project, outletPoints }, newLayer };
}
